<template>
  <div>
    <div class="header-row">
      <div class="header-main">
        <div class="title-row">
          <h1>{{ collateralLabel(debt.collateralProperty) }}</h1>
          <!-- Active is the normal state and goes unmarked; only a repaid debt is flagged. -->
          <AppChip v-if="debt.lifeCycle.status === 'repaid'" color="muted" dot>Återbetald</AppChip>
        </div>
        <p class="address text-subtle">{{ fullAddress }}</p>
      </div>
      <div class="header-actions">
        <OpenConversationsButton :debt-id="debt.id" />
        <DebtServicingActions :debt="debt" @saved="emit('saved', $event)" />
      </div>
    </div>

    <div v-if="sortedDebtors.length" class="debtors">
      <RouterLink
        v-for="debtor in sortedDebtors"
        :key="debtor.personalNumber"
        :to="`/kunder/${debtor.userId}`"
        class="debtor-box"
      >
        <div class="debtor-role-row">
          <span class="debtor-role">{{ debtor === mainDebtor ? 'Huvudlåntagare' : 'Medlåntagare' }}</span>
          <AppChip v-if="isInvoiceRecipient(debtor)" color="brand">Fakturamottagare</AppChip>
        </div>

        <div class="debtor-identity">
          <div class="debtor-name">{{ debtor.givenName }} {{ debtor.surname }}</div>
          <div class="text-subtle">{{ debtor.personalNumber }}</div>
        </div>

        <div class="debtor-contact">
          <span v-if="debtor.contactDetails.phone" class="contact-line">
            <SvgIcon :path="mdiPhoneOutline" size="16" class="contact-icon" />
            <span class="contact-value">{{ debtor.contactDetails.phone }}</span>
          </span>
          <span v-if="debtor.contactDetails.email" class="contact-line">
            <SvgIcon :path="mdiEmailOutline" size="16" class="contact-icon" />
            <span class="contact-value" :title="debtor.contactDetails.email">{{ debtor.contactDetails.email }}</span>
          </span>
          <span class="contact-line">
            <SvgIcon :path="mdiPercentOutline" size="16" class="contact-icon" />
            <span class="contact-value">Räntefördelning {{ formatPercentage(debtor.interestShareForTaxReporting) }}</span>
          </span>
        </div>
      </RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiEmailOutline, mdiPercentOutline, mdiPhoneOutline } from '@mdi/js'
import { AppChip, SvgIcon } from '@staff/ui'
import { formatPercentage } from '@staff/shared'
import OpenConversationsButton from '@/components/OpenConversationsButton.vue'
import DebtServicingActions from './components/DebtServicingActions.vue'
import type { Debt } from './debt.types'
import { collateralLabel } from './debt.utils'

const props = defineProps<{ debt: Debt }>()

const emit = defineEmits<{ saved: [debt: Debt] }>()

const fullAddress = computed(() => {
  if (!props.debt) return ''
  const addr = props.debt.collateralProperty.address
  const street = addr.streetNo ? `${addr.street} ${addr.streetNo}` : addr.street
  const streetPart = addr.apartmentNumber ? `${street} Lgh ${addr.apartmentNumber}` : street
  const cityPart = addr.zip ? `${addr.zip} ${addr.city}` : addr.city
  return `${streetPart}, ${cityPart}`
})

const mainDebtor = computed(() => {
  if (!props.debt) return null
  return props.debt.debtors.find((d) => d.roles.isMainDebtor) ?? props.debt.debtors[0] ?? null
})

const sortedDebtors = computed(() => {
  if (!props.debt || !mainDebtor.value) return []
  return [mainDebtor.value, ...props.debt.debtors.filter((d) => d !== mainDebtor.value)]
})

const isInvoiceRecipient = (debtor: { roles: { isInvoiceRecipient: boolean } }) => debtor.roles.isInvoiceRecipient
</script>

<style scoped lang="scss">
.header-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  column-gap: var(--space-16);
  row-gap: var(--space-8);
}

.header-main {
  min-width: 0;
}

.title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: var(--space-12);
  row-gap: var(--space-4);
}

.address {
  margin-top: var(--space-8);
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-8);
}

.debtors {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-12);
  margin-top: var(--space-16);
}

.debtor-box {
  display: flex;
  width: 340px;
  max-width: 100%;
  flex-direction: column;
  gap: var(--space-8);
  padding: var(--space-12) var(--space-16);
  border: 1px solid var(--color-border-muted);
  border-radius: var(--radius-md);
  background-color: var(--color-canvas-default);
  color: inherit;
  text-decoration: none;

  &:hover {
    border-color: var(--color-border-hover);
  }
}

// Reserves the chip's height so names line up whether or not the box has a chip.
.debtor-role-row {
  display: flex;
  min-height: var(--space-24);
  align-items: center;
  justify-content: space-between;
  gap: var(--space-8);
}

.debtor-role {
  font-size: var(--font-size-xs);
  line-height: var(--font-size-xs-line-height);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-fg-subtle);
}

.debtor-identity {
  min-width: 0;
}

.debtor-name {
  font-weight: var(--font-weight-semibold);
}

.debtor-contact {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--space-4);
}

.contact-line {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: var(--space-6);
}

.contact-icon {
  flex-shrink: 0;
  color: var(--color-icon-default);
}

.contact-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
